import Immutable from "immutable";
import moment from "moment";
import React from "react";

import {indexBy, indexByJs} from "js/common/utils/collections";
import * as DashboardUtils from "js/dashboards/utils";
import * as Users from "js/common/users";
import * as KpiRepo from "js/common/repo/backbone/kpi-repo";
import * as TimeframeRepo from "js/common/repo/backbone/timeframe-repo";

import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import {TextButton} from "js/common/views/inputs/buttons";
import Tooltip from "js/common/views/tooltips";
import {agGridChartFills, agGridChartStrokes} from "js/common/cube19-colors";
import {hasValue} from "js/common/utils/value-checking";
import {getIsDeepLinkable} from "js/common/utils/novo-comms";
import {themeAlpine} from "ag-grid-community";
import {currentQueryString} from "js/common/utils/query-strings";
import GridTooltip from "js/dashboards/components/grids/grid-tooltip";

// Workaround: ag-charts-enterprise 13.0.0 renders chart tooltips in the browser's
// top-layer using the CSS Anchor Positioning API. The anchor element lives inside a
// react-grid-layout tile that uses CSS transforms for positioning, which causes the
// browser to miscalculate the anchor coordinates (tooltip appears too high / too far left).
//
// The CSS half of this fix lives in ag-grid-ba.css — it disables position-anchor and
// translate, and reads --mouse-x/--mouse-y custom properties to position the tooltip
// centered horizontally and just above the cursor.
// This hook sets those properties on :root, throttled to the display refresh rate via rAF,
// and only while the mouse is over a chart container.
//
// TODO: Remove once ag-charts fixes anchor positioning inside transformed containers,
// or react-grid-layout stops using CSS transforms.
// Relevant versions: ag-charts-enterprise 13.0.0, react-grid-layout 1.x.
export const useChartTooltipFix = (chartElRef) => {
  React.useEffect(() => {
    const el = chartElRef?.current;
    if (!el) return;

    let rafId = null;
    let latestX = 0;
    let latestY = 0;

    const onMove = (e) => {
      latestX = e.clientX;
      latestY = e.clientY;
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          const root = document.documentElement.style;
          root.setProperty("--mouse-x", latestX + "px");
          root.setProperty("--mouse-y", latestY + "px");
          rafId = null;
        });
      }
    };
    const onEnter = () => {
      el.addEventListener("mousemove", onMove);
    };
    const onLeave = () => {
      el.removeEventListener("mousemove", onMove);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
      el.removeEventListener("mousemove", onMove);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [chartElRef]);
};

export const isNumber = x => typeof (x) === "number";

// Treat null, undefined, empty string, and whitespace-only strings as "blank" for grouping purposes
export const normalizeBlankValue = (value) => {
  if (value === null || value === undefined || (typeof value === "string" && value.trim() === "")) {
    return null;
  }
  return value;
};

const PERIOD_REGEX = /(\d{4})\/(\d{4})\s*-?\s*(\d{1,2})/;
const WEEK_REGEX = /Week of (\d{4})-(\d{2})-(\d{2})/;

const isValidMonth = month => month >= 1 && month <= 12;
const isValidDay = day => day >= 1 && day <= 31;

const monthToNumberMap = {
  "JANUARY": 1,
  "FEBRUARY": 2,
  "MARCH": 3,
  "APRIL": 4,
  "MAY": 5,
  "JUNE": 6,
  "JULY": 7,
  "AUGUST": 8,
  "SEPTEMBER": 9,
  "OCTOBER": 10,
  "NOVEMBER": 11,
  "DECEMBER": 12
};

const baseComparator = (a, b) => {
  a = a && a.hasOwnProperty("value") ? a.value : a;
  b = b && b.hasOwnProperty("value") ? b.value : b;
  if (!hasValue(a) && !hasValue(b)) {
    return 0;
  } else if (!hasValue(a) && hasValue(b)) {
    return -1;
  } else if (hasValue(a) && !hasValue(b)) {
    return 1;
  } else {
    if (isNumber(a) && isNumber(b)) {
      if (a < b) {
        return -1;
      } else if (a > b) {
        return 1;
      } else {
        return 0;
      }
    } else {
      return a.localeCompare(b, undefined, {numeric: true});
    }
  }
};

export const getGridAndChartWidth = (config) => {
  if (!config.hasIn(["chart", "position"])) {
    return {gridWidth: "100%", chartWidth: 0};
  } else if (config.has("chart") && config.getIn(["chart", "position"]) !== "NONE") {
    const chartPosition = config.getIn(["chart", "position"]);
    if (chartPosition === "LEFT") {
      const chartWidth = "50%";
      const gridWidth = "50%";
      return {gridWidth, chartWidth};
    } else {
      const chartWidth = "100%";
      const gridWidth = 0;
      return {gridWidth, chartWidth};
    }
  } else {
    return {gridWidth: "100%", chartWidth: 0};
  }
};

export const isScheduledReport = () => currentQueryString.scheduledreport?.toLowerCase() === "true";

export const chartIsPositioned = config => ["LEFT", "FULL"].includes(config.getIn(["chart", "position"]));

export const mountChart = (config, chartThemeName, containerDomNode, agGridApi, unlinked, sectorLabelFormatter, panelColor) => {
  if (!agGridApi) {
    return;
  }
  if (!chartIsPositioned(config)) {
    return;
  }
  const chartThemeOverrides = {
    common: {
      // A scheduled report prints the chart once it has rendered, which is before an
      // entrance animation has finished drawing it in, so there it must not animate.
      ...(isScheduledReport() ? {animation: {enabled: false}} : {}),
      title: {
        enabled: false
      },
      // With a panel colour set the chart background is hidden so the panel colour shows
      // through; otherwise it uses the theme's solid surface.
      background: panelColor
          ? {visible: false}
          : {fill: chartThemeName === "cube-theme" ? "#fff" : "#272731"},
      legend: {
        enabled: true,
        position: "bottom"
      },
      navigator: {
        enabled: true,
        height: 8
      }
    },
    bar: {
      axes: {
        "grouped-category": {
          left: {
            label: {
              rotation: 0
            }
          },
          right: {
            label: {
              rotation: 0
            }
          }
        }
      }
    }
  };
  if (sectorLabelFormatter) {
    const sectorLabelConfig = {
      series: {
        sectorLabel: {
          enabled: true,
          formatter: sectorLabelFormatter
        }
      }
    };
    chartThemeOverrides.pie = sectorLabelConfig;
    chartThemeOverrides.donut = sectorLabelConfig;

    const hierarchicalTooltipRenderer = (params) => {
      const formattedValue = sectorLabelFormatter(params);
      if (formattedValue === undefined) return undefined;
      const categoryLabel = params.datum?.[params.labelKey] ?? params.datum?.node?.key;
      return {
        title: params.sizeName || "",
        data: [{label: categoryLabel || "", value: formattedValue}]
      };
    };
    chartThemeOverrides.sunburst = {series: {tooltip: {renderer: hierarchicalTooltipRenderer}}};
    chartThemeOverrides.treemap = {series: {tooltip: {renderer: hierarchicalTooltipRenderer}}};
  }
  const pivotChartParams = {
    chartType: config.getIn(["chart", "type"], "stackedColumn"),
    chartContainer: containerDomNode,
    chartThemeName,
    unlinkChart: unlinked,
    chartThemeOverrides
  };
  return agGridApi.createPivotChart(pivotChartParams);
};

const defaultColDef = {
  sortable: true,
  filter: true,
  suppressHeaderFilterButton: true,
  enablePivot: true,
  enableRowGroup: true,
  enableValue: true,
  minWidth: 160,
  cellRendererParams: {
    suppressCount: true
  },
  menuTabs: ["generalMenuTab"],
  pivotComparator: (a, b) => baseComparator(a, b),
  comparator: (a, b) => baseComparator(a, b)
};

// The three dot menu is an edit-mode affordance; dragging is controlled at the grid
// level via suppressMovableColumns so that it stays reactive for server-side grids.
export const readOnlyAwareColDef = isEditing => ({
  ...defaultColDef,
  suppressHeaderMenuButton: !isEditing
});

// Pivot result columns set via setPivotResultColumns bypass defaultColDef merging, so
// suppressHeaderMenuButton must be applied directly to each node in the group tree.
export const applyMenuButtonVisibilityToColGroupTree = (groupOrColumn, isEditing) => {
  groupOrColumn.suppressHeaderMenuButton = !isEditing;
  if (groupOrColumn.children) {
    groupOrColumn.children.forEach(child => applyMenuButtonVisibilityToColGroupTree(child, isEditing));
  }
};

export const defaultGridProps = {
  defaultColDef,
  pivotMode: true,
  sideBar: false,
  enableCharts: true,
  groupMaintainOrder: false,
  groupDisplayType: "multipleColumns",
  suppressDragLeaveHidesColumns: true,
  suppressRowDrag: true,
  rowSelection: {
    mode: "singleRow",
    checkboxes: false,
    enableClickSelection: false
  },
  suppressCellFocus: true,
  suppressFocusAfterRefresh: true,
  tooltipShowDelay: 500,
  tooltipComponent: GridTooltip,
  chartThemes: ["cube-theme", "ag-material", "ag-material-dark"],
  chartToolPanelsDef: { panels: ["data", "settings"] },
  rowHeight: 30,
  suppressContextMenu: true,
  getContextMenuItems: () => [],
  autoGroupColumnDef: {
    tooltipValueGetter: params => params.value,
    menuTabs: ["generalMenuTab"],
    chartDataType: "category"
  }
};

export const customChartThemes = {
  "cube-theme": {
    baseTheme: "ag-material",
    palette: {
      fills: agGridChartFills,
      strokes: agGridChartStrokes
    },
    overrides: {
      common: {
        title: {
          enabled: false
        },
        subtitle: {
          enabled: false,
          fontSize: 12,
          fontFamily: "Monaco, monospace"
        }
      }
    }
  },
  "cube-theme-dark": {
    baseTheme: "ag-material-dark",
    palette: {
      fills: agGridChartFills,
      strokes: agGridChartStrokes
    },
    overrides: {
      common: {
        title: {
          enabled: false
        },
        subtitle: {
          enabled: false,
          fontSize: 12,
          fontFamily: "Monaco, monospace"
        }
      }
    }
  }
};

export const getRowGroupKeys = node => {
  const rowGroupKeys = [];
  let currentNode = node;
  // Use node.group to check if it's a group node, rather than hasValue(key)
  // This allows null keys (blank/normalized values) to be included
  // Check level >= 0 to exclude the root node (which has level: -1)
  while (currentNode && currentNode.group && currentNode.level >= 0) {
    rowGroupKeys.push(currentNode.key);
    currentNode = currentNode.parent;
  }
  rowGroupKeys.reverse();
  return rowGroupKeys;
};

export const getExpandedRowKeys = api => {
  const keys = [];
  api.forEachNode(node => {
    if (node.group && node.expanded) keys.push(Immutable.List(getRowGroupKeys(node)));
  });
  return Immutable.List(keys);
};

// Groups that no longer exist are simply never matched, so a saved expansion referring to
// a deleted group or user quietly does nothing rather than erroring.
export const restoreExpandedRowKeys = (api, savedKeys) => {
  if (!savedKeys || savedKeys.isEmpty()) return;
  const wanted = Immutable.Set(savedKeys);
  api.forEachNode(node => {
    if (node.group && !node.expanded && wanted.has(Immutable.List(getRowGroupKeys(node)))) {
      node.setExpanded(true);
    }
  });
};

const _matchingRowsCache = new WeakMap();

export const getMatchingRows = (
    rowGroupColIds,
    rowGroupKeys,
    pivotColIds,
    pivotKeys,
    pivotRows,
    quickFilterText = "",
    filterModel = {}
) => {
  let rowsCache = _matchingRowsCache.get(pivotRows);
  if (!rowsCache) {
    rowsCache = new Map();
    _matchingRowsCache.set(pivotRows, rowsCache);
  }
  const cacheKey = JSON.stringify([rowGroupKeys, pivotKeys, quickFilterText, filterModel]);
  if (rowsCache.has(cacheKey)) {
    return rowsCache.get(cacheKey);
  }

  const filterModelColIds = Object.keys(filterModel);
  for (let i = 0; i < filterModelColIds.length; i++) {
    const colId = filterModelColIds[i];
    const filter = filterModel[colId];
    filter.values = new Set(filter.values);
  }

  const filterWords = quickFilterText.split(" ").filter(x => !!x).map(x => x.toLowerCase());

  const result = pivotRows.filter(row => {
    const colIds = Object.keys(row);
    for (let i = 0; i < filterWords.length; i++) {
      const word = filterWords[i];
      let wordMatchesAtLeastOneCell = false;
      for (let j = 0; j < colIds.length; j++) {
        const colId = colIds[j];
        if ((row[colId].value + "").toLowerCase().indexOf(word) !== -1) {
          wordMatchesAtLeastOneCell = true;
        }
      }
      if (!wordMatchesAtLeastOneCell) {
        return false;
      }
    }

    for (let i = 0; i < filterModelColIds.length; i++) {
      const colId = filterModelColIds[i];
      const filter = filterModel[colId];
      if (filter.filterType !== "set" || !filter.values.has(row[colId]?.value)) {
        return false;
      }
    }
    for (let i = 0; i < Math.min(rowGroupKeys.length, rowGroupColIds.length); i++) {
      const rowGroupColId = rowGroupColIds[i];
      const rowGroupKey = normalizeBlankValue(rowGroupKeys[i]);
      const rowValue = row[rowGroupColId]?.value;

      // Note: More ag-grid weirdness
      // ag-grid creates group nodes with string keys so we need to handle
      // number mismatches like '13' vs 13. That's why we use loose equality
      // instead of strict equality here.
      // Also normalize rowGroupKey since ag-grid uses '' for blank groups but our data uses null
      // eslint-disable-next-line eqeqeq
      if (rowValue != rowGroupKey) {
        return false;
      }
    }
    for (let i = 0; i < Math.min(pivotKeys.length, pivotColIds.length); i++) {
      const pivotColId = pivotColIds[i];
      const pivotKey = normalizeBlankValue(pivotKeys[i]);
      // eslint-disable-next-line eqeqeq
      if (row[pivotColId]?.value != pivotKey) {
        return false;
      }
    }
    return true;
  });
  rowsCache.set(cacheKey, result);
  return result;
};

export const getFilterTitle = (rowGroupColIds, rowGroupKeys, pivotColIds, pivotKeys) => {
  let titleParts = [];
  for (let i = 0; i < Math.min(rowGroupKeys.length, rowGroupColIds.length); i++) {
    const rowGroupColId = rowGroupColIds[i];
    const rowGroupKey = rowGroupKeys[i];
    titleParts.push(rowGroupColId + ": " + rowGroupKey);
  }
  for (let i = 0; i < Math.min(pivotKeys.length, pivotColIds.length); i++) {
    const pivotColId = pivotColIds[i];
    const pivotKey = pivotKeys[i];
    titleParts.push(pivotColId + ": " + pivotKey);
  }
  return titleParts.join(", ");
};

const DAY_NAME_TO_INDEX = {
  SUNDAY: 0, MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3, THURSDAY: 4, FRIDAY: 5, SATURDAY: 6
};

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const buildDayToOrder = weekStartsOn => {
  const startIndex = DAY_NAME_TO_INDEX[weekStartsOn?.toUpperCase()] ?? 1;
  return DAYS_OF_WEEK.reduce((acc, day, i) => {
    acc[day] = ((i - startIndex + 7) % 7) + 1;
    return acc;
  }, {});
};

export const createDayOfWeekComparator = weekStartsOn => {
  const dayToOrder = buildDayToOrder(weekStartsOn);
  return (a, b) => {
    const aVal = (a?.value ?? a) ?? null;
    const bVal = (b?.value ?? b) ?? null;
    if (aVal === null && bVal === null) return 0;
    if (aVal === null) return -1;
    if (bVal === null) return 1;
    return (dayToOrder[aVal] || 0) - (dayToOrder[bVal] || 0);
  };
};

export const dataTypeToValueFormat = dataType => {
  if (dataType === "PERCENTAGE") {
    return "PERCENT";
  } else if (dataType === "CURRENCY") {
    return "CURRENCY";
  } else {
    return "NUMBER";
  }
};

export const DownloadButton = React.memo(({onClick}) => {
  if (Users.currentHasPermission("EXPORT_FILE")) {
    return <TextButton testId="grid-download" label="Download" type="inverted" onClick={onClick} style={{zIndex: 1}} />;
  } else {
    return <div style={{display: "inline-block"}}>
      <Tooltip
          text="Ask an admin user for the 'Export To File' permission to download this data"
          position="right">
        <TextButton label="Download" disabled={true} type="inverted" onClick={() => {}} />
      </Tooltip>
    </div>;
  }
});

export const getExportFileName = (config, componentTitle) => {
  let name;
  if (config.has("fileName")) {
    name = config.get("fileName", "").replaceAll(": ", " - ");
  } else if (componentTitle) {
    name = componentTitle;
  } else {
    name = config.get("kpiIds", Immutable.List()).map(kpiId => KpiRepo.get(kpiId).get("name")).join(", ");
  }
  return name + ".xlsx";
};

export const mergeFilterModels = (savedModel, currentModel) => {
  if (!savedModel || savedModel.isEmpty()) {
    return currentModel;
  }
  if (!currentModel || currentModel.isEmpty()) {
    return savedModel;
  }

  let merged = savedModel;

  currentModel.forEach((currentFilter, key) => {
    if (!savedModel.has(key)) {
      // New filter field from data change — add it
      merged = merged.set(key, currentFilter);
    } else {
      const savedFilter = savedModel.get(key);
      if (savedFilter.get("filterType") === "set" && currentFilter.get("filterType") === "set") {
        // Union of values: keep saved selections and add any new values from the data change
        const savedValues = savedFilter.get("values", Immutable.List());
        const currentValues = currentFilter.get("values", Immutable.List());
        const savedSet = savedValues.toOrderedSet();
        const unionValues = savedSet.union(currentValues.toOrderedSet()).toList();
        merged = merged.setIn([key, "values"], unionValues);
      }
      // For non-set filters, keep the saved version (already in merged)
    }
  });

  return merged;
};

// Restore a saved filter model after a data change, intersecting set filter values
// with what's actually available in the current data. This avoids "No matching rows"
// when the saved filter contains values not present in the new data set.
// filterModelRef is kept as the full saved filter separately for future restores.
export const restoreFilterModel = (savedModel, currentModel) => {
  if (!savedModel || savedModel.isEmpty()) {
    return currentModel;
  }
  if (!currentModel) {
    return Immutable.Map();
  }
  if (currentModel.isEmpty()) {
    return Immutable.Map();
  }

  let restored = Immutable.Map();

  savedModel.forEach((savedFilter, key) => {
    const currentFilter = currentModel.get(key);
    if (!currentFilter) {
      // This filter column no longer exists in the grid, skip
      return;
    }
    if (savedFilter.get("filterType") === "set" && currentFilter.get("filterType") === "set") {
      const savedValues = savedFilter.get("values", Immutable.List()).toSet();
      const availableValues = currentFilter.get("values", Immutable.List()).toSet();
      const matchingValues = savedValues.intersect(availableValues);

      if (matchingValues.size > 0 && matchingValues.size < availableValues.size) {
        // Some saved values exist in the data and it's not "select all" — apply filter
        restored = restored.set(key, savedFilter.set("values", matchingValues.toList()));
      }
      // If no saved values exist in current data: don't add filter (show all rows)
      // If all values match: no point filtering, same as no filter
    } else {
      // Non-set filter: restore as-is
      restored = restored.set(key, savedFilter);
    }
  });

  return restored;
};

// Restores a saved columnOrder into the grid. Called on initial load and after
// setPivotResultColumns resets the column model in the server-side grid.
export const applyColumnOrder = (api, columnOrder) => {
  if (api && columnOrder && columnOrder.size > 0) {
    api.applyColumnState({
      state: columnOrder.toJS().map(colId => ({colId})),
      applyOrder: true,
    });
  }
};

const autoGroupColumnPrefix = "ag-Grid-AutoColumn-";

const isAutoGroupColumn = colId => colId.indexOf(autoGroupColumnPrefix) === 0;

const nonUserColumnEventSources = Immutable.Set([
  "api",
  "gridInitializing",
  "gridOptionsChanged",
  "rowModelUpdated",
  "rowDataUpdated",
  "cellDataTypeInferred",
  "alignedGridChanged",
  "flex",
  "pivotChart"
]);

const isUserColumnEvent = source => !source || !nonUserColumnEventSources.has(source);

const groupedColIdsByRowGroupIndex = columnStates => columnStates
    .filter(state => state.get("rowGroup"))
    .sortBy(state => state.get("rowGroupIndex"))
    .map(state => state.get("colId"));

const orderColumnStatesByRowGroupIndex = columnStates => {
  const orderedColIds = groupedColIdsByRowGroupIndex(columnStates);
  const stateByColId = indexBy(state => state.get("colId"), columnStates);
  let nextGroup = 0;
  return columnStates.map(state => state.get("rowGroup")
      ? stateByColId.get(orderedColIds.get(nextGroup++))
      : state);
};

const orderAutoGroupColumns = (colIds, orderedGroupColIds) => {
  const expected = orderedGroupColIds.map(colId => autoGroupColumnPrefix + colId);
  if (colIds.filter(isAutoGroupColumn).size !== expected.size) {
    return expected.concat(colIds.filterNot(isAutoGroupColumn));
  }
  let nextGroup = 0;
  return colIds.map(colId => isAutoGroupColumn(colId) ? expected.get(nextGroup++) : colId);
};

const applyDisplayedGroupOrderToStates = (columnStates, colIds) => {
  const orderedColIds = colIds
      .filter(isAutoGroupColumn)
      .map(colId => colId.substring(autoGroupColumnPrefix.length));
  if (orderedColIds.isEmpty()) {
    return columnStates;
  }
  const indexByColId = Immutable.Map(orderedColIds.map((colId, i) => [colId, i]));
  return orderColumnStatesByRowGroupIndex(columnStates.map(state => indexByColId.has(state.get("colId"))
      ? state.set("rowGroupIndex", indexByColId.get(state.get("colId")))
      : state));
};

// Only reacts to finished user drags (source === "uiColumnMoved")
// api/gridOptionsChanged sources from applyColumnState calls are ignored.
export const handleColumnMovedImpl = (e, componentId, onConfigChange, columnLabels) => {
  if (e.finished && e.source === "uiColumnMoved") {
    const newColumnStates = getColumnStatesFromApi(e.api, columnLabels);
    // Use getAllDisplayedColumns so secondary (pivot result) columns are included —
    // getColumnState() only returns primary columns and misses them entirely.
    const newColumnOrder = Immutable.List(e.api.getAllDisplayedColumns().map(c => c.getColId()));
    onConfigChange(componentId, config => config
        .set("columnStates", applyDisplayedGroupOrderToStates(newColumnStates, newColumnOrder))
        .set("columnOrder", newColumnOrder));
  }
};

export const keepGridStateInSync = (componentId, columnLabels, onConfigChange, filterModelRef, pendingFilterRestoreRef) => {
  return {
    onFilterChanged: (e) => {
      const filterModel = Immutable.fromJS(e.api.getFilterModel());
      // NOTE We have to update a ref here to avoid regenerating column states on every filter change.
      // If we don't use a ref then ag-grid will reset the ui state of the filter panel on every change.
      // This makes it very difficult to use because all open accordions get closed.
      const isApiChange = e.source === "api";

      if (filterModelRef) {
        // When data changes (e.g. dashboard timeframe change), ag-grid's set filters
        // recalculate asynchronously and fire onFilterChanged. ag-grid defers these
        // events via setTimeout(fn, 0) inside callOnFilterChangedOutsideRenderCycle.
        // We debounce with a longer delay (100ms) to ensure ALL of ag-grid's deferred
        // events have settled before we restore the user's saved filter selection.
        // A 0ms timeout would race with ag-grid's own setTimeout(0) and lose.
        const restore = pendingFilterRestoreRef?.current;
        if (restore) {
          clearTimeout(restore.timer);
          restore.timer = setTimeout(() => {
            pendingFilterRestoreRef.current = null;
            const currentModel = Immutable.fromJS(e.api.getFilterModel());
            const restored = restoreFilterModel(restore.savedFilter, currentModel);
            filterModelRef.current = restore.savedFilter;
            if (!restored.isEmpty() && !Immutable.is(restored, currentModel)) {
              e.api.setFilterModel(restored.toJS());
            }
          }, 100);
          return;
        }

        if (isApiChange) {
          onConfigChange(componentId, (config) =>
              config.set("filterModel", filterModelRef.current)
          );
        } else {
          filterModelRef.current = filterModel;
          onConfigChange(componentId, (config) =>
              config.set("filterModel", filterModel)
          );
        }
      } else {
        onConfigChange(componentId, (config) =>
            config.set("filterModel", filterModel));
      }
    },
    onColumnRowGroupChanged: e => {
      const newColumnStates = getColumnStatesFromApi(e.api, columnLabels);
      if (!isUserColumnEvent(e.source)) {
        onConfigChange(componentId, config => config.set("columnStates", newColumnStates));
        return;
      }
      const orderedGroupColIds = groupedColIdsByRowGroupIndex(newColumnStates);
      const displayedColIds = e.api.getAllDisplayedColumns
          ? Immutable.List(e.api.getAllDisplayedColumns().map(c => c.getColId()))
          : null;
      onConfigChange(
          componentId,
          config => config
              .set("columnStates", orderColumnStatesByRowGroupIndex(newColumnStates))
              .set(
                  "columnOrder",
                  orderAutoGroupColumns(
                      displayedColIds || config.get("columnOrder", Immutable.List()),
                      orderedGroupColIds)));
    },
    onColumnValueChanged: e => {
      onConfigChange(
          componentId,
          config => config.set("columnStates", getColumnStatesFromApi(e.api, columnLabels)));
    },
    onSortChanged: e => {
      const colState = e.api.getColumnState();
      const sortState = colState
          .filter(function(s) {
            return s.sort != null;
          })
          .map(function(s) {
            return {colId: s.colId, sort: s.sort, sortIndex: s.sortIndex};
          });
      onConfigChange(componentId, config => config.set("colOrder", Immutable.List(sortState)));
    },
    onColumnPivotChanged: e => {
      onConfigChange(
          componentId,
          config => config.set("columnStates", getColumnStatesFromApi(e.api, columnLabels)));
    },
    onColumnPivotModeChanged: e => {
      onConfigChange(componentId, config => config.setIn(["gridProps", "pivotMode"], e.api.isPivotMode()));
    },
    onColumnVisible: e => {
      onConfigChange(
          componentId,
          config => config.set("columnStates", getColumnStatesFromApi(e.api, columnLabels)));
    },
    onColumnPinned: e => {
      onConfigChange(
          componentId,
          config => config.set("columnStates", getColumnStatesFromApi(e.api, columnLabels)));
    },
    onColumnMoved: e => {
      onConfigChange(
          componentId,
          config => config.set("columnStates", getColumnStatesFromApi(e.api, columnLabels)));
    }
  };
};

export const getColumnStatesFromApi = (api, validColumnLabels) => {
  const columnState = api.getColumnState();
  if (!columnState) {
    return Immutable.fromJS([]);
  }
  return Immutable.fromJS(api.getColumnState().flatMap(c => {
    const shouldKeepInState = (c.rowGroup || c.pivot || !!c.aggFunc || !c.hide)
        && validColumnLabels.has(c.colId);
    if (shouldKeepInState) {
      return [c];
    } else {
      return [];
    }
  }));
};

const defaultAllowedAggFuncs = ["sum", "avg", "count", "min", "max"];

const dataTypeToAllowedAggFuncs = {
  BOOLEAN: ["countUnique", "sum", "avg", "count", "min", "max"],
  INTEGER: ["sum", "avg", "count", "countUnique", "min", "max"],
  CURRENCY: ["sum", "avg", "min", "max"],
  STRING: ["countUnique", "count", "min", "max"],
  DATE: ["countUnique", "count", "min", "max"],
  PERCENTAGE: ["avg", "sum", "min", "max"]
};

const getValueGetterFn = valueGetter => params => {
  const cellData = params.data && params.data[params.colDef.colId];
  const deepLinkingData = cellData && cellData.deepLinkingData;
  if (getIsDeepLinkable(deepLinkingData)) {
    return `Click to open the ATS record for ${deepLinkingData.crmEntityName} ${deepLinkingData.crmId} in a new tab`;
  } else {
    return valueGetter(params);
  }
};

const getCellClass = params => {
  const cellData = params.data && params.data[params.colDef.colId];
  const deepLinkingData = cellData && cellData.deepLinkingData;
  return getIsDeepLinkable(deepLinkingData) ? "ag-deep-linked-cell" : null;
};

const getAggregationFunctionsForAsyncServerGrid = (aggregations) => {
  return aggregations.filter(func => func !== "countUnique");
};

export const dataTypeToChartType = {
  DATE: "time",
  STRING: "category",
  INTEGER: "series",
  PERCENTAGE: "series",
  CURRENCY: "series",
  BOOLEAN: "category"
};

const numericResultAggFuncs = new Set(["count", "countUnique", "sum", "avg"]);

export const columnsToDefs = (columns, columnStates, valueGetterForKey, valueFormatterForKey, financialYearStartMonth, gridType, weekStartsOn) => {
  const isUiGrid = gridType === "uiGrid";
  const labelToColumn = indexByJs(c => c.label, columns);
  const columnLabelToState = indexBy(
      s => s.get("colId"),
      Immutable
          .fromJS(columnStates)
          .map((state, i) => {
            const context = state.get("context") || Immutable.Map();
            const order = context.get("order") || (i + 1);
            return state.set("context", context.set("order", order));
          })
  ).toJS();

  return columns
      .slice()
      .filter(c => c.columnFor === "UI")
      .map(c => {
        const state = columnLabelToState[c.label] || {};
        const valueGetter = valueGetterForKey(c.label);
        const aggregationFunctionsForDataType = dataTypeToAllowedAggFuncs[c.dataType] || defaultAllowedAggFuncs;
        const customAggregationFunctions = c.customAggregationFunctions || [];
        const aggregationFunctions = customAggregationFunctions.concat(aggregationFunctionsForDataType);

        const finalAggregationFunctions = isUiGrid
            ? aggregationFunctions
            : getAggregationFunctionsForAsyncServerGrid(aggregationFunctions);

        const defaultAggFunc = finalAggregationFunctions[0];
        const effectiveAggFunc = state.aggFunc || defaultAggFunc;
        const usedAsDimension = !!state.rowGroup || !!state.pivot;
        const dataType = !usedAsDimension && numericResultAggFuncs.has(effectiveAggFunc)
            ? "series"
            : (dataTypeToChartType[c.dataType] || null);

        // Get custom comparator if needed, at the moment this only supports dates
        // but could be extended for any type of data
        const customComparator = getColumnComparator(c, financialYearStartMonth, weekStartsOn);

        const stateAttributes = {
          ...state,
          ...(!!state.sortType
              ? {context: {...state.context, sortType: state.sortType}}
              : {})
        };

        delete stateAttributes.sortType;

        return {
          chartDataType: dataType,
          hide: true,
          filter: "agSetColumnFilter",
          ...stateAttributes,
          allowedAggFuncs: finalAggregationFunctions,
          defaultAggFunc,
          cellClass: getCellClass,
          colId: c.label,
          headerName: c.label,
          comparator: customComparator || c.comparator,
          pivotComparator: customComparator || c.comparator,
          valueFormatter: valueFormatterForKey(c.label, labelToColumn),
          valueGetter,
          tooltipValueGetter: getValueGetterFn(valueGetter),
          filterValueGetter: valueGetter
        };
      })
      .sort((a, b) => {
        if (hasValue(a.context?.order) && hasValue(b.context?.order)) {
          return a.context.order - b.context.order;
        } else if (a.context?.order) {
          return -1;
        } else if (b.context?.order) {
          return 1;
        } else {
          return a.headerName.localeCompare(b.headerName);
        }
      });
};

export const createGroupOrderComparator = weekStartsOn => {
  const dayToOrder = buildDayToOrder(weekStartsOn);
  return (params) => {
    const colId = params.nodeA.rowGroupColumn?.colId;
    if (colId?.endsWith("(Day of Week)")) {
      const aKey = params.nodeA.key;
      const bKey = params.nodeB.key;
      if (!hasValue(aKey) && !hasValue(bKey)) return 0;
      if (!hasValue(aKey)) return -1;
      if (!hasValue(bKey)) return 1;
      return (dayToOrder[aKey] || 0) - (dayToOrder[bKey] || 0);
    }
    if (hasValue(params.nodeA.key) && hasValue(params.nodeB.key)) {
      return params.nodeA.key.localeCompare(params.nodeB.key);
    } else if (params.nodeA.key) {
      return 1;
    } else if (params.nodeB.key) {
      return -1;
    } else {
      return 0;
    }
  };
};

export const configToReqParams = config => {

  const timeframe = TimeframeRepo.parse(config.get("timeframe").toJS());
  return {
    dateFromUI: moment().format("YYYY-MM-DD"),
    startDate: timeframe.get("start").format("YYYY-MM-DD"),
    endDate: timeframe.get("end").format("YYYY-MM-DD"),
    clientIds: config.getIn(["clientFilter", "allClientIds"], Immutable.List()).toArray(),
    anyOfTheseTagIds: config.getIn(["tagFilter", "matchAnyTagIds"], Immutable.List()).toArray(),
    allOfTheseTagIds: config.getIn(["tagFilter", "matchAllTagIds"], Immutable.List()).toArray(),
    noneOfTheseTagIds: config.getIn(["tagFilter", "excludedTagIds"], Immutable.List()).toArray(),
    entityToIds: (config.get("entityToIds") ?? Immutable.Map()).toObject(),
    ...DashboardUtils.configToQualifierAjaxParams(config)
  };
};

export const addRequiredColumns = (request, columns) => {
  const labelToColumn = indexByJs(c => c.label, columns);

  const allGridColumns = []
      .concat(request.rowGroupCols)
      .concat(request.pivotCols)
      .concat(request.valueCols)
      .concat(request.dataCols);
  for (const gridColumn of allGridColumns) {
    gridColumn.requiredColumns = [];
    const column = labelToColumn[gridColumn.displayName];
    // NOTE column may not be defined if the grid config is out of sync with the currently available columns
    //  perhaps the kpis on the grid changed or the columns on the kpis changed
    if (column) {
      for (const columnBeforeMerge of column.columnsBeforeMerge) {
        gridColumn.requiredColumns.push({
          serverColumn: columnBeforeMerge.serverColumn,
          entityColumnId: columnBeforeMerge.entityColumnId,
          joinPathStr: columnBeforeMerge.joinPathStr,
          derivedFor: columnBeforeMerge.derivedFor
        });
      }
    }
  }
};

// TODO: Move to dashboard utils
// TODO: Change variant to not be a string
// TODO: Add hover shadow - work with zach
// TODO: Make min width 1080
export const ComponentTile = React.memo(({variant = "standard", panelColor = null, children}) => {
  const {theme} = React.useContext(CustomThemeContext);
  // Panel "match": when set, the tile surface uses a shade of the dashboard's
  // selected background colour instead of the default theme card colour.
  const tileStyle = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    backgroundColor: variant === "transparent" ? "transparent" : (panelColor || theme.palette.background.card),
    borderRadius: variant === "transparent" ? 0 : 5,
    overflow: variant !== "standard" ? "hidden" : "visible",
    boxShadow: variant === "transparent"
        ? "none"
        : "0px 0.3px 0.9px rgba(0, 0, 0, 0.1), 0px 1.6px 3.6px rgba(0, 0, 0, 0.13)"
  };

  // With a panel colour set, the c19-bg-panel class makes any enclosed ag-grid transparent
  // (with a translucent row stripe) so the panel colour shows through — see ag-grid-ba.css.
  // Without it, grids keep their default light/dark surface.
  return <div style={tileStyle} className={panelColor ? "c19-bg-panel" : undefined}>
    {children}
  </div>;
});

export const openChartToolPanelsOnEdit = (isEditing, gridRef) => {
  if (!gridRef || !gridRef.current) {
    return;
  }

  const api = gridRef.current.api;
  // checking for api makes sure the chart and required elements exist
  if (!api) {
    return;
  }
  const gridCharts = api.getChartModels();
  if (isEditing) {
    gridCharts.forEach(c => api.openChartToolPanel({chartId: c.chartId, panel: "settings"}));
  } else {
    gridCharts.forEach(c => api.closeChartToolPanel({chartId: c.chartId}));
  }
};

export const reMountChart = (config, gridRef, chartTheme, chartElRef, unlinked, sectorLabelFormatter, panelColor) => {
  const api = gridRef.current?.api;
  if (!api) {
    return;
  }
  const gridCharts = api.getChartModels();
  gridCharts.forEach(c => api.getChartRef(c.chartId).destroyChart());
  mountChart(config, chartTheme, chartElRef.current, api, unlinked, sectorLabelFormatter, panelColor);
};

export const getChartShapeSignature = (columnDefs) =>
    (columnDefs || [])
        .map(def => `${def.colId}:${def.chartDataType || ""}`)
        .sort()
        .join("|");

export const getThemes = themeId => {
  return {
    gridTheme: themeId === "light" ? "ag-theme-alpine" : "ag-theme-alpine-dark",
    chartTheme: themeId === "light" ? "cube-theme" : "cube-theme-dark"
  };
};

export const chartThemes = ["cube-theme", "cube-theme-dark"];

export const parsePeriod = (str) => {
  // Expected formats:
  // "YYYY/YYYY - MM"
  // "YYYY/YYYY MM"
  const match = str.match(PERIOD_REGEX);
  if (!match) {
    return null;
  }
  const [, startYear, endYear, month] = match;

  const startYearNum = parseInt(startYear);
  const endYearNum = parseInt(endYear);
  const monthNum = parseInt(month);

  if (!isValidMonth(monthNum) || endYearNum !== startYearNum + 1) {
    return null;
  }
  return {
    startYear: parseInt(startYear),
    endYear: parseInt(endYear),
    month: monthNum
  };
};

export const parseWeekPeriod = (str) => {
  // format: "Week of YYYY-MM-DD"
  const match = str.match(WEEK_REGEX);
  if (!match) {
    return null;
  }
  const [, year, month, day] = match;
  const monthNum = parseInt(month);
  const dayNum = parseInt(day);
  if (!isValidMonth(monthNum) || !isValidDay(dayNum)) {
    return null;
  }
  return {
    year: parseInt(year),
    month: monthNum,
    day: dayNum
  };
};

const compareOptionalValues = (a, b) => {
  if (a === b) {
    return 0;
  } else if (a == null) {
    return -1;
  } else if (b == null) {
    return 1;
  } else {
    return null;
  }
};

const getMonthPosition = (month, fyStart) => (month - fyStart + 12) % 12;

const createPeriodParser = (parser) => (a, b) => {
  const aRaw = (a?.value ?? a) ?? null;  // Convert undefined to null, edge case
  const bRaw = (b?.value ?? b) ?? null;
  const result = compareOptionalValues(aRaw, bRaw);

  if (result !== null) {
    return result;
  }

  const aVal = aRaw.toString();
  const bVal = bRaw.toString();

  const periodA = parser(aVal);
  const periodB = parser(bVal);
  if (!periodA || !periodB) {
    return aVal.localeCompare(bVal, undefined, {numeric: true});
  }
  return [periodA, periodB];
};

export const createFinancialYearComparator = (financialYearStartMonth) => {
  return (a, b) => {
    const result = createPeriodParser(parsePeriod, financialYearStartMonth)(a, b);
    if (!Array.isArray(result)) {
      return result;
    }

    const [periodA, periodB] = result;
    if (periodA.startYear !== periodB.startYear) {
      return periodA.startYear - periodB.startYear;
    }

    const posA = getMonthPosition(periodA.month, financialYearStartMonth);
    const posB = getMonthPosition(periodB.month, financialYearStartMonth);
    return posA - posB;
  };
};

const getFiscalYear = (year, month, fyStart) => {
  return month < fyStart ? year - 1 : year;
};

export const createWeekComparator = (financialYearStartMonth) => {
  return (a, b) => {
    const result = createPeriodParser(parseWeekPeriod, financialYearStartMonth)(a, b);
    if (!Array.isArray(result)) {
      return result;
    }
    const [periodA, periodB] = result;

    const fyYearA = getFiscalYear(periodA.year, periodA.month, financialYearStartMonth);
    const fyYearB = getFiscalYear(periodB.year, periodB.month, financialYearStartMonth);

    if (fyYearA !== fyYearB) {
      return fyYearA - fyYearB;
    }

    const posA = getMonthPosition(periodA.month, financialYearStartMonth);
    const posB = getMonthPosition(periodB.month, financialYearStartMonth);
    if (posA !== posB) {
      return posA - posB;
    }

    return periodA.day - periodB.day;
  };
};

export const getColumnComparator = (column, financialYearStartMonth, weekStartsOn) => {
  const fyStartMonth = monthNameToNumber(financialYearStartMonth);

  if (column.label?.endsWith("(Month)")) {
    return createFinancialYearComparator(fyStartMonth);
  }
  if (column.label?.endsWith("(Week)")) {
    return createWeekComparator(fyStartMonth);
  }
  if (column.label?.endsWith("(Day of Week)")) {
    return createDayOfWeekComparator(weekStartsOn);
  }
  return null;
};

export const monthNameToNumber = (monthName) => {
  if (typeof monthName !== "string") {
    throw new Error("Month name must be a string");
  }

  const monthNumber = monthToNumberMap[monthName.toUpperCase()];
  if (!monthNumber) {
    throw new Error(`Invalid month name: ${monthName}`);
  }

  return monthNumber;
};

// When a dashboard panel colour is set the grid surface goes transparent so that colour
// (applied to the enclosing ComponentTile) shows through; the row striping is then driven
// by the --c19-row-* CSS variables in ag-grid-ba.css. With no panel colour the default
// light/dark theme surface is used, unchanged.
export const getThemeAlpine = (theme, panelColor) => {
  return themeAlpine.withParams({
    backgroundColor: panelColor ? "transparent" : "#1f2836",
    fontFamily: theme.typography.fontFamily,
    fontSize: 13.6,
    dataFontSize: 13.6,
    headerFontSize: 12
  });
};

export const getChartThemeOverrides = (theme, panelColor) => ({
  common: {
    title: {
      enabled: false
    },
    background: panelColor
        ? {visible: false}
        : {fill: theme.themeId === "light" ? "#fff" : "#272731"},
    legend: {
      enabled: true,
      position: "bottom"
    },
    navigator: {
      enabled: true,
      height: 8
    }
  },
  pie: {
    series: {
      sectorLabel: {
        enabled: true
      }
    }
  },
  donut: {
    series: {
      sectorLabel: {
        enabled: true
      }
    }
  }
});
